import { useToastStatus as ToastStatusGlobal } from '@/components/toastStatusGlobal';
import { Company } from '@/modules/company/entities/interfaces/company';
import { USAGE_STATEMENT_STATUS_FULL } from '@/modules/copay/entities/enums/usageStatement';
import { UsageStatementProcess } from '@/modules/copay/entities/interfaces/usageStatements';
import { useUsageStatementProcess } from '@/modules/copay/hooks/useUsageStatements/useUsageStatementProcess';
import useFileDownload from '@/modules/files/hooks/useFileDownload/useFileDownload';
import { useHistoryNavigator } from '@/navigation';
import ICONS from '@/shared/entities/constants/icons';
import React, { Dispatch, SetStateAction } from 'react';
import { Button, Popover } from 'vkit/lib/components';
import { Grid } from 'vkit/lib/context';

interface ActionsByStatusProps {
  usageStatementProcess: UsageStatementProcess;
  processing?: boolean;
  refreshData?: () => void;
  companyParentId?: string;
  company?: Company;
  setOpenDialogShareCopay: Dispatch<SetStateAction<boolean>>;
  setUsageStatementWithWaitingDescriptionStatus: Dispatch<
    SetStateAction<UsageStatementProcess | null>
  >;
}

export const ActionsByStatus: React.FC<ActionsByStatusProps> = ({
  usageStatementProcess,
  refreshData,
  processing,
  companyParentId,
  company,
  setOpenDialogShareCopay,
  setUsageStatementWithWaitingDescriptionStatus,
}) => {
  const { toast } = ToastStatusGlobal();
  const { loading, process, publish } = useUsageStatementProcess({
    callbackPublishSuccess: () => {
      refreshData?.();
      toast(
        'Feito',
        `Arquivo enviado para pasta de faturamento da empresa ${company?.name}.`,
        'success',
      );
    },
    callbackProcessSuccess: (payload, processedUsageStatement) => {
      let messageSucess = 'Isenção de coparticipação iniciada.';

      switch (payload.approve) {
        case false:
          messageSucess = 'Isenção de coparticipação recusado.';
          break;
        case null:
          messageSucess = 'Isenção de coparticipação retomada.';
          break;
      }

      refreshData?.();

      if (processedUsageStatement.status === USAGE_STATEMENT_STATUS_FULL.WAITING_DESCRIPTION) {
        setUsageStatementWithWaitingDescriptionStatus(processedUsageStatement);
        setOpenDialogShareCopay(true);
        return;
      }
      toast('Feito', messageSucess, 'success');
    },
  });
  const navigate = useHistoryNavigator();
  const { downloadFile, loading: loadingDownload } = useFileDownload();
  const isStatusError = usageStatementProcess.status === USAGE_STATEMENT_STATUS_FULL.ERROR;

  if (usageStatementProcess.status === USAGE_STATEMENT_STATUS_FULL.NOT_PROCESSED && !processing) {
    return (
      <Grid gap={6} alignContent='right' alignItems='center' stretch>
        <Button
          loading={loading}
          color='success'
          label='Aprovar'
          icon={ICONS.CHECK}
          onClick={() => process(usageStatementProcess.id, { approve: true })}
        />
        <Button
          loading={loading}
          color='danger'
          label='Recusar'
          icon={ICONS.CLOSE}
          onClick={() => process(usageStatementProcess.id, { approve: false })}
        />
      </Grid>
    );
  }

  if (usageStatementProcess.status === USAGE_STATEMENT_STATUS_FULL.REFUSED && !processing) {
    return (
      <Grid gap={6} alignContent='right' alignItems='center' stretch>
        <Button
          loading={loading}
          label='Retomar'
          icon={ICONS.FLIP}
          onClick={() => process(usageStatementProcess.id, { approve: null })}
        />
      </Grid>
    );
  }

  return (
    <Grid gap={6} alignContent='right' alignItems='center' stretch>
      <Button
        iconColor={usageStatementProcess.processing || isStatusError ? 'light' : 'colorDefault'}
        loading={loading || loadingDownload}
        label='Ver detalhes'
        icon={ICONS.DETAILS}
        onClick={() => navigate.push(`/copaies/${usageStatementProcess.id}/details`)}
        solo
        outlined
        disabled={usageStatementProcess.processing || isStatusError}
      />

      <Popover
        vertical
        actions={[
          {
            label: 'Baixar resultado de coparticipação',
            icon: ICONS.DOWNLOAD,
            disabled:
              usageStatementProcess.processing ||
              ![
                USAGE_STATEMENT_STATUS_FULL.PROCESSED,
                USAGE_STATEMENT_STATUS_FULL.FINISHED,
              ].includes(usageStatementProcess.status),
            onClick: () => {
              if (usageStatementProcess.resultFileId) {
                downloadFile(usageStatementProcess.resultFileId, companyParentId);
              } else {
                toast('Oops!', 'Nenhum arquivo disponível para download!', 'error');
              }
            },
          },
          {
            label: 'Enviar arquivo para faturamento',
            icon: ICONS.SEND,
            disabled:
              usageStatementProcess.processing ||
              usageStatementProcess.status !== USAGE_STATEMENT_STATUS_FULL.PROCESSED,
            onClick: () => publish(usageStatementProcess.id),
          },
        ]}
      />
    </Grid>
  );
};
